/**
 * Остатки склада — стилизованы под кладовщик-Excel «Складской учет 20».
 *
 * Структура из эксельки:
 * - Шапка: название склада (выбор), кнопки Склад/Печать, корзина с счётчиком,
 *   per-column поиск (Артикул / Наименование / Примечания).
 * - Таблица: Артикул | Наименование | Ед. | Кол-во | Бронь | Критич. Ост. | Цена закуп | Цена розн. | Примечания
 *   Группировка по разделам (Дерево, Кровля, …) — заголовки секций жирным красным.
 *   В каждой строке — компактные кнопки + / − для добавления в корзину.
 * - Низкий остаток подсвечен красным; нулевой — серым.
 */
import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { CartSidebar } from "@/components/warehouse/CartSidebar";
import { EditableCell } from "@/components/warehouse/EditableCell";
import {
  fetchStocks, fetchStocksSummary, fetchUnits,
  patchStock, patchItem,
  formatRub, formatQty, type StockRow,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";
import { useWarehouseCart } from "@/state/warehouseCart";


export function WarehouseStocksPage() {
  const [warehouse, setWarehouse] = useState<"СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС">("СКЛАД 2_4");
  const [searchSku, setSearchSku] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchNote, setSearchNote] = useState("");
  // ?low=1 в URL → автостарт с фильтром «только низкий остаток»
  // (используется ссылкой с виджета «🔴 Ниже критич.» на Сводке).
  const [lowOnly, setLowOnly] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("low") === "1"; }
    catch { return false; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const cart = useWarehouseCart();

  const qc = useQueryClient();
  const stocksQ = useQuery({
    queryKey: ["warehouse-stocks", warehouse, lowOnly],
    queryFn: () => fetchStocks({ warehouse, low_only: lowOnly, limit: 5000 }),
  });
  const summaryQ = useQuery({
    queryKey: ["warehouse-stocks-summary", warehouse],
    queryFn: () => fetchStocksSummary({ warehouse }),
  });
  const unitsQ = useQuery({
    queryKey: ["warehouse-units"],
    queryFn: () => fetchUnits(),
  });

  // Мутации: PATCH /stocks/{id} и /items/{id}. Используем оптимистик-апдейт
  // через invalidate (простой и надёжный — нет рейсов с другими полями).
  const stockMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof patchStock>[1] }) =>
      patchStock(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-stocks-summary"] });
    },
  });
  const itemMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof patchItem>[1] }) =>
      patchItem(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-items"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-units"] });
    },
  });

  function saveStock(id: number, field: "reserved" | "critical_qty" | "price_buy" | "price_sell" | "note", v: string | null) {
    let parsed: number | string | null = v;
    if (field !== "note") {
      if (v === null || v === "") {
        parsed = null;
      } else {
        const n = parseFloat(v.replace(",", ".").replace(/\s/g, ""));
        if (!isFinite(n)) return;
        parsed = n;
      }
    }
    stockMut.mutate({ id, patch: { [field]: parsed } as Parameters<typeof patchStock>[1] });
  }

  function saveItemUnit(itemId: number, v: string | null) {
    itemMut.mutate({ id: itemId, patch: { unit: v } });
  }

  const rawItems = stocksQ.data?.items || [];

  // Локальный поиск по 3 колонкам — клиентский, без перезапроса (быстрее)
  const items = useMemo(() => {
    const sku = searchSku.trim().toLowerCase();
    const nm = searchName.trim().toLowerCase();
    const nt = searchNote.trim().toLowerCase();
    if (!sku && !nm && !nt) return rawItems;
    return rawItems.filter(it => {
      if (sku && !(it.sku || "").toLowerCase().includes(sku)) return false;
      if (nm && !(it.name || "").toLowerCase().includes(nm)) return false;
      if (nt && !(it.note || "").toLowerCase().includes(nt)) return false;
      return true;
    });
  }, [rawItems, searchSku, searchName, searchNote]);

  const grouped = useMemo(() => {
    const out: Record<string, StockRow[]> = {};
    for (const it of items) {
      const sec = it.section || "Без раздела";
      if (!out[sec]) out[sec] = [];
      out[sec].push(it);
    }
    return out;
  }, [items]);

  if (stocksQ.isLoading || summaryQ.isLoading) return <Spinner size="lg" label="Загрузка остатков…" />;
  if (stocksQ.error) {
    const err = stocksQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }

  function addToCart(it: StockRow, delta: number) {
    if (it.item_id === undefined || it.item_id === null) return;
    cart.add({
      item_id: it.item_id,
      sku: it.sku,
      name: it.name || "—",
      unit: it.unit,
      warehouse: it.warehouse,
      quantity: delta,
      price: delta > 0 ? it.price_buy : it.price_sell,
    });
  }

  return (
    <div className="page">

      {/* Top toolbar — стиль кладовщика */}
      <div className="card" style={{ padding: "12px 16px", marginBottom: "var(--gap)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, color: "var(--brick)" }}>
            {warehouse}
          </div>
          <select
            className="input-sm"
            value={warehouse}
            onChange={e => setWarehouse(e.target.value as "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС")}
            style={{ minWidth: 200 }}
          >
            <option value="СКЛАД 2_4">СКЛАД 2_4</option>
            <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
            <option value="ОФИС">ОФИС</option>
          </select>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-3)" }}>
            <input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} />
            Только низкий остаток
          </label>
          <div style={{ flex: 1 }} />
          <button
            type="button" className="btn btn-sm"
            onClick={() => window.print()}
            title="Печать таблицы"
          >
            <Icon name="report" size={12}/> Печать
          </button>
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: cart.count > 0 ? "var(--brick)" : "var(--card)",
              color: cart.count > 0 ? "#fff" : "var(--ink)",
              border: `1px solid ${cart.count > 0 ? "var(--brick)" : "var(--line)"}`,
              padding: "8px 14px", borderRadius: 6, cursor: "pointer",
              fontWeight: 600, fontSize: 13,
            }}
          >
            <Icon name="tag" size={14}/> <span>{cart.count}</span>
            {cart.total > 0 && <span style={{ opacity: 0.85, fontWeight: 400 }}>· {formatRub(cart.total)}</span>}
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="row row-4" style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Позиций</div>
          <div className="kpi-value kpi-value-sm">{items.length}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>в выборке</span></div>
        </div>
        <div className="kpi good">
          <div className="kpi-label">В наличии</div>
          <div className="kpi-value kpi-value-sm">{summaryQ.data?.in_stock || 0}</div>
        </div>
        <div className={`kpi ${(summaryQ.data?.low_stock || 0) > 0 ? "alarm" : ""}`}>
          <div className="kpi-label">Низкий остаток</div>
          <div className="kpi-value kpi-value-sm">{summaryQ.data?.low_stock || 0}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Стоимость (закуп)</div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(summaryQ.data?.value_buy || 0, { unit: false })}
            <span className="kpi-unit">{Math.abs(summaryQ.data?.value_buy || 0) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
        </div>
      </div>

      {/* Таблица в стиле кладовщика */}
      <div className="card" style={{ padding: 0 }}>
        <div className="tbl-scroll">
          <table className="tbl kladovshchik">
            <thead>
              <tr>
                <th style={{ width: 130 }}>
                  Артикул
                  <div style={{ marginTop: 4 }}>
                    <input type="search" className="input-sm" placeholder="поиск…" value={searchSku}
                           onChange={e => setSearchSku(e.target.value)} style={{ width: "100%", fontSize: 11 }} />
                  </div>
                </th>
                <th>
                  Наименование
                  <div style={{ marginTop: 4 }}>
                    <input type="search" className="input-sm" placeholder="поиск…" value={searchName}
                           onChange={e => setSearchName(e.target.value)} style={{ width: "100%", fontSize: 11 }} />
                  </div>
                </th>
                <th style={{ width: 60 }}>Ед.</th>
                <th style={{ width: 120 }} className="num">Кол-во</th>
                <th style={{ width: 70 }} className="num">Бронь</th>
                <th style={{ width: 90 }} className="num">Критич. ост.</th>
                <th style={{ width: 100 }} className="num">Цена закуп</th>
                <th style={{ width: 100 }} className="num">Цена розн.</th>
                <th>
                  Примечания
                  <div style={{ marginTop: 4 }}>
                    <input type="search" className="input-sm" placeholder="поиск…" value={searchNote}
                           onChange={e => setSearchNote(e.target.value)} style={{ width: "100%", fontSize: 11 }} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([sec, rows]) => (
                <Fragment key={sec}>
                  <tr>
                    <td colSpan={9} style={{
                      background: "var(--paper)", fontWeight: 700,
                      color: "var(--brick-deep)", fontSize: 13, padding: "8px 10px",
                    }}>
                      {sec} <span style={{ color: "var(--mute)", fontWeight: 400 }}>· {rows.length}</span>
                    </td>
                  </tr>
                  {rows.map(r => {
                    const isZero = r.quantity <= 0;
                    const isLow  = !isZero && r.critical_qty !== null && r.quantity <= (r.critical_qty || 0);
                    return (
                      <tr key={r.id} style={{ opacity: isZero ? 0.55 : 1 }}>
                        <td className="tabular" style={{ fontSize: 12 }}>{r.sku || "—"}</td>
                        <td style={{ fontSize: 12 }}>{r.name || "—"}</td>
                        <td style={{ fontSize: 12, padding: 0 }}>
                          {r.item_id ? (
                            <EditableCell
                              kind="select" value={r.unit ?? ""}
                              options={unitsQ.data || []}
                              onSave={(v) => saveItemUnit(r.item_id, v)}
                              align="left"
                            />
                          ) : (r.unit || "—")}
                        </td>
                        <td className="num">
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <button
                              type="button" title="Списать в корзину расхода"
                              onClick={() => addToCart(r, -1)}
                              style={miniBtnStyle("var(--brick)")}
                            >−</button>
                            <span className="tabular strong" style={{
                              minWidth: 36, textAlign: "center",
                              color: isLow ? "var(--brick)" : isZero ? "var(--mute)" : "var(--ink)",
                            }}>
                              {formatQty(r.quantity, null)}
                            </span>
                            <button
                              type="button" title="Добавить в корзину прихода"
                              onClick={() => addToCart(r, 1)}
                              style={miniBtnStyle("var(--green)")}
                            >+</button>
                          </div>
                        </td>
                        <td className="num" style={{ padding: 0, fontSize: 12 }}>
                          <EditableCell
                            kind="number" value={r.reserved > 0 ? r.reserved : null} align="right"
                            onSave={(v) => saveStock(r.id, "reserved", v)}
                            format={(v) => v === null || v === 0 ? "—" : String(v)}
                          />
                        </td>
                        <td className="num" style={{ padding: 0, fontSize: 12,
                                                    color: isLow ? "var(--brick)" : undefined }}>
                          <EditableCell
                            kind="number" value={r.critical_qty} align="right"
                            onSave={(v) => saveStock(r.id, "critical_qty", v)}
                            format={(v) => v === null ? "—" : String(v)}
                          />
                        </td>
                        <td className="num" style={{ padding: 0, fontSize: 12 }}>
                          <EditableCell
                            kind="number" value={r.price_buy} align="right"
                            onSave={(v) => saveStock(r.id, "price_buy", v)}
                            format={(v) => v === null ? "—" : Number(v).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          />
                        </td>
                        <td className="num" style={{ padding: 0, fontSize: 12 }}>
                          <EditableCell
                            kind="number" value={r.price_sell} align="right"
                            onSave={(v) => saveStock(r.id, "price_sell", v)}
                            format={(v) => v === null ? "—" : Number(v).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          />
                        </td>
                        <td style={{ fontSize: 11, color: "var(--mute)", padding: 0 }}>
                          <EditableCell
                            kind="text" value={r.note} align="left"
                            onSave={(v) => saveStock(r.id, "note", v)}
                            placeholder=""
                          />
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: "var(--mute)" }}>
                  Позиций не найдено
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CartSidebar open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}


function miniBtnStyle(color: string): React.CSSProperties {
  return {
    width: 22, height: 22, padding: 0,
    background: "var(--card)", color, border: `1px solid ${color}`,
    borderRadius: 4, fontWeight: 700, cursor: "pointer", lineHeight: 1,
    fontSize: 14,
  };
}
